import { useState } from 'react';
import { OnboardingShell } from '../OnboardingShell/OnboardingShell';
import {
  OnboardingV2Provider,
  useOnboardingV2OrServerFlag,
} from '../OnboardingShell/onboardingV2';
import { StepCta, StepHeading, StepStack, StepSub } from '../OnboardingShell/StepLayout';
import { OptionRow } from '../primitives';
import { STEP_META } from '../../fixtures/onboarding';
import type { ChapterProgress } from '../../lib/onboardingChapters';
import {
  EXPERIENCE_OPTIONS,
  INJURY_OPTIONS,
  MILEAGE_BANDS,
  bandById,
  bandFromMiles,
  bandLabelIn,
  bandLowerIn,
  buildTrainingDataPatch,
  checkExactWeekly,
  injuryNoteProblem,
  injuryText,
  type DistanceUnit,
  type InjuryOptionId,
  type MileageBandId,
  type MissingItem,
} from '../../lib/firstPlan/trainingData';
import styles from './CollectionBeat.module.css';

type CollectionBeatProps = {
  /** Which data-gate items to ask for, in gate order (already filtered by the
   *  route to only the MISSING ones). */
  missing: MissingItem[];
  /**
   * Persist the answered patch to athlete_context, then start generation.
   * The CALLER decides how: `/plan/draft` awaits its own persist and re-
   * enables the CTA via `error` on failure; `/plan/first-look` (B148) hands
   * the patch straight to the athlete-context provider's own fire-and-forget
   * `mergeContext` and moves on, so `busy`/`error` stay unset there.
   */
  onComplete: (patch: Record<string, unknown>) => void;
  /** True while the completion persist is in flight (CTA → "Building…"). */
  busy?: boolean;
  /** Inline error after a failed persist (CTA stays enabled for retry). */
  error?: string | null;
  /**
   * F-520 — back handler for the FIRST question. In the first-look flow there
   * is nothing before the beat, so it stays undefined (no back on Q1). When the
   * beat runs IN the wizard (before the guardian beat / Welcome), the wizard
   * passes this so Q1's Back returns to the previous wizard step.
   */
  onBackFromFirst?: () => void;
  /**
   * F-520 — label for the LAST question's CTA. Defaults to "Build my plan"
   * (first-look, where the next thing IS generation). In the wizard the beat is
   * followed by the guardian beat / Welcome, so the wizard passes "Continue".
   */
  lastCtaLabel?: string;
  /**
   * O1 — the beat's place in the chaptered onboarding flow, when it runs IN the
   * wizard. The beat is the TAIL OF CHAPTER 3 there, so its questions are part
   * of that chapter's denominator and it wears the chapter's segments.
   *
   * The route passes the position of the beat's FIRST question; this component
   * is the only thing that knows which of its own questions is on screen, so it
   * offsets by its own index before handing the shell a position.
   *
   * ⛔ Undefined in FIRST-LOOK, which is not a chaptered flow at all — the beat
   * keeps its existing chrome there, segment-free, exactly as it ships today.
   */
  chapter?: ChapterProgress;
  /**
   * B457 — the unit the weekly distance is shown and asked in. The wizard passes
   * the athlete's chosen unit (else the location preselect) and
   * `onDistanceUnitChange`; the bands then read in that unit and a mi / km toggle
   * shows above them. Band ids, the stored miles and the first-look path are
   * unchanged: with neither prop this is exactly the shipped beat (miles bands, no
   * toggle there, the exact box starting on miles).
   */
  distanceUnit?: DistanceUnit;
  onDistanceUnitChange?: (unit: DistanceUnit) => void;
};

// The gate's stable order — the collection beat asks in this sequence.
const ORDER: MissingItem[] = ['current weekly mileage', 'running experience', 'injury status'];

/**
 * SP-3 — the data-gate collection beat. One question per screen, only the
 * missing ones, in gate order. Reuses the wizard's OnboardingShell `metaOverride`
 * chrome + StepLayout/OptionRow grammar so it reads as if the wizard always had
 * these questions (founder UX-consistency note). No skip control — the plan
 * cannot be authored without these answers (data-gate principle).
 */
export function CollectionBeat({
  missing,
  onComplete,
  busy,
  error,
  onBackFromFirst,
  lastCtaLabel = 'Build my plan',
  chapter,
  distanceUnit,
  onDistanceUnitChange,
}: CollectionBeatProps) {
  // Part 7: this beat also mounts on /plan/first-look and /plan/draft, outside the
  // onboarding route and its provider, so it answers for itself (the route's answer when
  // there is one, else the server flag) and hands that to its own shell and children.
  const v2 = useOnboardingV2OrServerFlag();
  const questions = ORDER.filter((q) => missing.includes(q));
  const [index, setIndex] = useState(0);

  // Mileage
  const [band, setBand] = useState<MileageBandId | null>(null);
  const [exactMode, setExactMode] = useState(false);
  const [exactStr, setExactStr] = useState('');
  const [exactUnit, setExactUnit] = useState<DistanceUnit>('mi');
  // B457 — the unit in force: the caller's when it passes one, else the exact
  // box's own (today's behaviour on the first-look path).
  const unit: DistanceUnit = distanceUnit ?? exactUnit;
  const chooseUnit = (next: DistanceUnit) => {
    setExactUnit(next);
    onDistanceUnitChange?.(next);
    if (exactStr.trim() !== '') setExactTouched(true);
  };
  // B441 — the sentence for a bad entry shows once the athlete has left the
  // box (or changed the unit under a number), never while a valid number is
  // still being typed.
  const [exactTouched, setExactTouched] = useState(false);
  // Experience
  const [expId, setExpId] = useState<string | null>(null);
  // Injury
  const [injuryId, setInjuryId] = useState<InjuryOptionId | null>(null);
  const [injuryDetail, setInjuryDetail] = useState('');
  // B464 — the optional note's limit; shown once she leaves the box or presses
  // Continue, then follows the value (the one rule for field sentences, B460).
  const [injuryTouched, setInjuryTouched] = useState(false);
  const injuryProblem =
    injuryId && injuryId !== 'none' ? injuryNoteProblem(injuryDetail) : null;

  const current = questions[index];
  const isLast = index === questions.length - 1;

  // B441 (rulings 2026-10-21 item 12) — digits with at most one decimal point,
  // 1 up to the per-unit ceiling (the wizard payload's own 250 miles). The
  // first-look path used to store 5000 where the wizard dropped it.
  const exactCheck = exactMode ? checkExactWeekly(exactStr, unit) : null;
  const exactValid = exactCheck?.ok === true;
  const exactNum = exactCheck?.ok ? exactCheck.value : 0;
  const exactMiles = exactCheck?.ok ? exactCheck.miles : null;
  const exactProblem =
    exactCheck && !exactCheck.ok && !exactCheck.empty ? exactCheck.error : null;

  const canContinue = (() => {
    if (current === 'current weekly mileage') return exactMode ? exactValid : !!band;
    if (current === 'running experience') return !!expId;
    if (current === 'injury status') return !!injuryId && !injuryProblem;
    return false;
  })();

  // UX M3 — a wrong number in the exact box is named by pressing Continue (the
  // Step 4 pattern: pressable, `aria-disabled`, "touched" on press), not only on
  // blur: a decimal pad has no Return. With nothing wrong to name (an empty box,
  // or a question that is simply unanswered) the button stays natively disabled.
  const explainable =
    (current === 'current weekly mileage' && exactMode && !!exactProblem) ||
    (current === 'injury status' && !!injuryProblem);

  const goNext = () => {
    if (busy) return;
    if (!canContinue) {
      if (explainable) {
        setExactTouched(true);
        setInjuryTouched(true);
      }
      return;
    }
    if (!isLast) {
      setIndex((i) => i + 1);
      return;
    }
    const patch = buildTrainingDataPatch({
      band: questions.includes('current weekly mileage')
        ? exactMode
          ? bandFromMiles(exactMiles as number)
          : band
        : null,
      exactMiles: exactMode ? exactMiles : null,
      experienceLabel: questions.includes('running experience')
        ? EXPERIENCE_OPTIONS.find((o) => o.id === expId)?.label ?? null
        : null,
      injuriesText: questions.includes('injury status')
        ? injuryText(injuryId as InjuryOptionId, injuryDetail)
        : null,
    });
    onComplete(patch);
  };

  const ctaLabel = busy ? 'Building your plan…' : isLast ? lastCtaLabel : 'Continue';

  /** The exact weekly box. Under Part 7 it is wrapped so the unit label can sit inside it. */
  const exactInput = (
    <input
      type="text"
      inputMode="decimal"
      className={styles.exactInput}
      value={exactStr}
      onChange={(e) => setExactStr(e.target.value)}
      onBlur={() => setExactTouched(true)}
      aria-label="Exact weekly distance"
      aria-invalid={exactTouched && exactProblem ? true : undefined}
      aria-describedby={exactTouched && exactProblem ? 'exact-weekly-problem' : undefined}
      placeholder="18"
    />
  );

  /** The mi / km toggle: beside the exact box, and (wizard only) above the bands. */
  const unitPill = (
    <div className={styles.unitPill} role="group" aria-label="Distance unit">
      {(['mi', 'km'] as DistanceUnit[]).map((u) => (
        <button
          key={u}
          type="button"
          className={unit === u ? styles.unitActive : styles.unit}
          onClick={() => chooseUnit(u)}
          aria-pressed={unit === u}
        >
          {u}
        </button>
      ))}
    </div>
  );

  return (
    <OnboardingV2Provider value={v2}>
    <OnboardingShell
      current={5}
      onBack={index > 0 ? () => setIndex((i) => i - 1) : onBackFromFirst}
      // O1 — the route hands down the position of the beat's FIRST question, so
      // the offset by `index` is what makes "Question 4 of 6 in this chapter"
      // move as the athlete walks the beat.
      chapter={
        chapter
          ? { ...chapter, questionIndex: (chapter.questionIndex ?? 1) + index }
          : undefined
      }
      // In the chaptered wizard the panel copy comes from chapter 3; this
      // override is what first-look wears, and the shell prefers it when both
      // are present — so it is passed ONLY when there is no chapter.
      metaOverride={
        chapter
          ? undefined
          : {
              stepLabel: 'Before your plan',
              label: 'A couple of quick details',
              context: 'Your plan is built from three answers. Airo never guesses them.',
              ambientBg: STEP_META[4].ambientBg,
            }
      }
    >
      {/* O1 — the beat's own dot row is its FIRST-LOOK progress signal. Inside
          the chaptered wizard the shell already shows the four segments and
          "Question N of M in this chapter", and two counters on one screen
          disagree by construction (this one counts the beat, that one counts
          the chapter). The chaptered flow keeps the chapter's. */}
      {!chapter && (
        <div className={styles.progressRow}>
          <div className={styles.dots} aria-hidden>
            {questions.map((q, i) => (
              <span key={q} className={i === index ? styles.dotActive : styles.dot} />
            ))}
          </div>
          <span className={styles.progressLabel}>
            {index + 1} of {questions.length}
          </span>
        </div>
      )}

      {current === 'current weekly mileage' && (
        <>
          <StepHeading>Roughly how far are you running per week right now?</StepHeading>
          <StepSub>
            Pick the closest band. Your first week starts from its lower edge, never above it.
          </StepSub>

          {!exactMode ? (
            <>
              {onDistanceUnitChange && <div className={styles.unitRow}>{unitPill}</div>}
              <StepStack>
                {MILEAGE_BANDS.map((b) => (
                  <OptionRow
                    key={b.id}
                    label={bandLabelIn(b, unit)}
                    sub={b.sub}
                    selected={band === b.id}
                    onClick={() => setBand(b.id)}
                  />
                ))}
              </StepStack>
              <button type="button" className={styles.hatchLink} onClick={() => setExactMode(true)}>
                I know my exact number &rsaquo;
              </button>
            </>
          ) : (
            <div className={styles.exactRow}>
              {v2 ? (
                <div className={styles.exactBox}>
                  {exactInput}
                  <span className={styles.exactUnit} aria-hidden="true">
                    {unit}
                  </span>
                </div>
              ) : (
                exactInput
              )}
              {unitPill}
              <button
                type="button"
                className={styles.useBands}
                onClick={() => {
                  setExactMode(false);
                  setExactStr('');
                  setExactTouched(false);
                }}
              >
                Use bands instead
              </button>
            </div>
          )}

          {/* B460 — the sentence's room is reserved, so it appearing never moves the
              line below it. B465 — and it replaces the grey helper rather than
              stacking above it: two lines about one box said the same thing twice. */}
          {exactMode && (
            <div className={styles.problemSlot}>
              {exactTouched && exactProblem && (
                <p id="exact-weekly-problem" role="alert" className={styles.fieldProblem}>
                  {exactProblem}
                </p>
              )}
            </div>
          )}

          {!(exactMode && exactTouched && exactProblem) && (
            <div className={styles.gateChip}>
              {exactMode
                ? exactValid
                  ? `Your plan starts from ${exactNum} ${unit} a week.`
                  : 'Enter your exact weekly distance.'
                : band
                  ? `Your plan starts from ${bandLowerIn(bandById(band), unit)} ${unit} a week, the lower edge of this band.`
                  : 'Pick a band to see where your plan starts.'}
            </div>
          )}
        </>
      )}

      {current === 'running experience' && (
        <>
          <StepHeading>How long have you been running consistently?</StepHeading>
          <StepStack>
            {EXPERIENCE_OPTIONS.map((o) => (
              <OptionRow
                key={o.id}
                label={o.label}
                selected={expId === o.id}
                onClick={() => setExpId(o.id)}
              />
            ))}
          </StepStack>
        </>
      )}

      {current === 'injury status' && (
        <>
          <StepHeading>Any current or recent injuries I should plan around?</StepHeading>
          <StepSub>&ldquo;None&rdquo; is a perfectly good answer.</StepSub>
          <StepStack>
            {INJURY_OPTIONS.map((o) => (
              <OptionRow
                key={o.id}
                label={o.label}
                selected={injuryId === o.id}
                onClick={() => {
                  setInjuryId(o.id);
                  // B465 — "No injuries" drops a note typed under another choice,
                  // so it cannot come back when she changes her mind again.
                  if (o.id === 'none') {
                    setInjuryDetail('');
                    setInjuryTouched(false);
                  }
                }}
              />
            ))}
          </StepStack>
          {injuryId && injuryId !== 'none' && (
            <>
              <input
                type="text"
                className={styles.detailInput}
                value={injuryDetail}
                onChange={(e) => setInjuryDetail(e.target.value)}
                onBlur={() => setInjuryTouched(true)}
                placeholder="Optional: where, and how it feels"
                aria-label="Injury detail (optional)"
                aria-invalid={injuryTouched && injuryProblem ? true : undefined}
                aria-describedby={injuryTouched && injuryProblem ? 'injury-note-problem' : undefined}
              />
              <div className={styles.problemSlot}>
                {injuryTouched && injuryProblem && (
                  <p id="injury-note-problem" role="alert" className={styles.fieldProblem}>
                    {injuryProblem}
                  </p>
                )}
              </div>
            </>
          )}
        </>
      )}

      <div className={styles.ctaWrap}>
        <StepCta
          onClick={goNext}
          disabled={(!canContinue && !explainable) || busy}
          aria-disabled={explainable ? true : undefined}
        >
          {ctaLabel}
        </StepCta>
      </div>

      {error && (
        <div className={styles.errorChip} role="alert" aria-live="assertive">
          {error}
        </div>
      )}

      <p className={styles.footer}>
        Why we ask: your plan cannot be written without these answers, and we never guess them.
      </p>
    </OnboardingShell>
    </OnboardingV2Provider>
  );
}
